import {
  seedCategories,
  seedProducts,
  isSeedId,
} from "../Font/data/seedCatalog";

// ─── KEYS ────────────────────────────────────────────────────────────────────
const CATEGORIES_KEY = "tecniqa_categories";
const PRODUCTS_KEY = "tecniqa_products";
// Edits/deletes applied to bundled seed records. Seed data itself is immutable
// (it ships in the JS bundle), so admin changes to it are stored as patches.
const SEED_OVERRIDES_KEY = "tecniqa_seed_overrides";

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const generateId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const readJson = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) =>
  localStorage.setItem(key, JSON.stringify(value));

// Convert name → url-safe slug  e.g. "AC Spares" → "ac-spares"
export const toSlug = (name) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-");

// ─── SEED OVERRIDES ──────────────────────────────────────────────────────────
const getOverrides = () => {
  const stored = readJson(SEED_OVERRIDES_KEY, {});
  return {
    categories: stored.categories || {},
    products: stored.products || {},
  };
};

const setOverride = (bucket, id, patch) => {
  const all = getOverrides();
  all[bucket] = {
    ...all[bucket],
    [id]: { ...(all[bucket][id] || {}), ...patch },
  };
  writeJson(SEED_OVERRIDES_KEY, all);
};

// Merge seed records with any admin patches, dropping ones marked deleted.
const withOverrides = (seedList, bucket) => {
  const patches = getOverrides()[bucket];
  return seedList
    .filter((item) => !patches[item.id]?.deleted)
    .map((item) => (patches[item.id] ? { ...item, ...patches[item.id] } : item));
};

// ─── CATEGORIES ──────────────────────────────────────────────────────────────
// Bundled seed categories first, then anything created via the admin panel.
export const getCategories = () => [
  ...withOverrides(seedCategories, "categories"),
  ...readJson(CATEGORIES_KEY, []),
];

export const saveCategory = (category) => {
  const all = readJson(CATEGORIES_KEY, []);
  const now = new Date().toISOString();
  const entry = {
    id: generateId(),
    name: category.name.trim(),
    slug: category.slug || toSlug(category.name),
    eyebrow: category.eyebrow || "Industrial Grade",
    description: category.description || "",
    isActive: true,
    createdAt: now,
  };
  all.push(entry);
  writeJson(CATEGORIES_KEY, all);
  return entry;
};

export const updateCategory = (id, updates) => {
  const updatedAt = new Date().toISOString();
  if (isSeedId(id)) {
    setOverride("categories", id, { ...updates, updatedAt });
    return;
  }
  const all = readJson(CATEGORIES_KEY, []).map((c) =>
    c.id === id ? { ...c, ...updates, updatedAt } : c
  );
  writeJson(CATEGORIES_KEY, all);
};

export const toggleCategoryActive = (id) => {
  const current = getCategories().find((c) => c.id === id);
  if (!current) return;
  updateCategory(id, { isActive: !current.isActive });
};

export const deleteCategory = (id) => {
  if (isSeedId(id)) {
    setOverride("categories", id, { deleted: true });
  } else {
    writeJson(
      CATEGORIES_KEY,
      readJson(CATEGORIES_KEY, []).filter((c) => c.id !== id)
    );
  }
  // Cascade: remove products in that category, seed and admin-created alike.
  seedProducts
    .filter((p) => p.categoryId === id)
    .forEach((p) => setOverride("products", p.id, { deleted: true }));
  writeJson(
    PRODUCTS_KEY,
    readJson(PRODUCTS_KEY, []).filter((p) => p.categoryId !== id)
  );
};

export const getCategoryBySlug = (slug) =>
  getCategories().find((c) => c.slug === slug && c.isActive !== false) || null;

// Returns only active categories for frontend/header use
export const getActiveCategories = () =>
  getCategories().filter((c) => c.isActive !== false);

// ─── PRODUCTS ────────────────────────────────────────────────────────────────
// Bundled seed products first, then anything created via the admin panel.
export const getProducts = () => [
  ...withOverrides(seedProducts, "products"),
  ...readJson(PRODUCTS_KEY, []),
];

export const getProductsByCategory = (categoryId) =>
  getProducts().filter((p) => p.categoryId === categoryId);

export const getProductsBySlug = (categorySlug) => {
  const cat = getCategoryBySlug(categorySlug);
  if (!cat) return [];
  return getProductsByCategory(cat.id).filter((p) => p.isActive !== false);
};

export const getProductById = (id) =>
  getProducts().find((p) => p.id === id) || null;

// imageFile → base64 string (returns Promise)
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const saveProduct = async (formData, imageFiles) => {
  const all = readJson(PRODUCTS_KEY, []);
  const [img1, img2, img3] = await Promise.all([
    fileToBase64(imageFiles.image1),
    fileToBase64(imageFiles.image2),
    fileToBase64(imageFiles.image3),
  ]);
  const entry = {
    id: generateId(),
    categoryId: formData.category?.id || "",
    categorySlug: formData.category?.slug || "",
    name: formData.productsName.trim(),
    brand: formData.brand.trim(),
    badge: formData.badge || "New Arrival",
    shortDescription: formData.shortDescription.trim(),
    description: formData.description,
    highlights: formData.highlights,
    mrp: formData.mrp,
    salePrice: formData.salePrice,
    discount: formData.discount,
    availability: formData.availability.trim(),
    warranty: formData.warranty.trim(),
    metaTitle: formData.metaTitle.trim(),
    metaDescription: formData.metaDescription.trim(),
    seoUrl: formData.seoUrl.trim(),
    images: [img1, img2, img3].filter(Boolean),
    isActive: true,
    createdAt: new Date().toISOString(),
  };
  all.push(entry);
  writeJson(PRODUCTS_KEY, all);
  return entry;
};

export const updateProduct = (id, updates) => {
  const updatedAt = new Date().toISOString();
  if (isSeedId(id)) {
    setOverride("products", id, { ...updates, updatedAt });
    return;
  }
  const all = readJson(PRODUCTS_KEY, []).map((p) =>
    p.id === id ? { ...p, ...updates, updatedAt } : p
  );
  writeJson(PRODUCTS_KEY, all);
};

export const toggleProductActive = (id) => {
  const current = getProducts().find((p) => p.id === id);
  if (!current) return;
  updateProduct(id, { isActive: !current.isActive });
};

export const deleteProduct = (id) => {
  if (isSeedId(id)) {
    setOverride("products", id, { deleted: true });
    return;
  }
  writeJson(
    PRODUCTS_KEY,
    readJson(PRODUCTS_KEY, []).filter((p) => p.id !== id)
  );
};
